"use client";

/**
 * Ad Tracking Utilities
 * Centralized tracking for Meta Pixel, Google Ads, LinkedIn Insight Tag
 * Safe to call on both server and client - server calls are no-ops
 */

export type TrackingEvent =
	| "PageView"
	| "Lead"
	| "Contact"
	| "SubmitForm"
	| "Subscribe"
	| "Download"
	| "InitiateCheckout"
	| "Purchase"
	| "ViewContent"
	| "Search"
		| "AddToCart"
		| "SubmitApplication"
		| "RfqSubmit"
		| "QuoteSubmit"
		| "DiagnosticSubmit";

export interface TrackingParams {
	currency?: string;
	value?: number;
	content_name?: string;
	content_category?: string;
	content_ids?: string[];
	email?: string;
	[ key: string ]: unknown;
}

// Pixel IDs - loaded from env / config, with safe fallbacks
const getPixelConfig = () => {
	if (typeof window === "undefined") return null;
	const w = window as unknown as {
		__AD_PIXELS__?: {
			meta?: string;
			google_ads?: string;
			linkedin?: string;
		};
	};
	return w.__AD_PIXELS__ || null;
};

export const isMetaPixelLoaded = (): boolean => {
	if (typeof window === "undefined") return false;
	const w = window as unknown as { fbq?: ( ...args: unknown[] ) => void };
	return typeof w.fbq === "function";
};

export const isGtagLoaded = (): boolean => {
	if (typeof window === "undefined") return false;
	const w = window as unknown as { gtag?: ( ...args: unknown[] ) => void };
	return typeof w.gtag === "function";
};

export const isLinkedInLoaded = (): boolean => {
	if (typeof window === "undefined") return false;
	const w = window as unknown as { lintrk?: ( ...args: unknown[] ) => void };
	return typeof w.lintrk === "function";
};

/**
 * Track an event across all active ad platforms
 */
export function trackEvent( event: TrackingEvent, params: TrackingParams = {} ): void {
	if (typeof window === "undefined") return;

	const config = getPixelConfig();
	if (!config) return;

	// Meta Pixel
	if (config.meta && isMetaPixelLoaded()) {
		const w = window as unknown as { fbq: ( ...args: unknown[] ) => void };
		w.fbq( "track", event, params );
	}

	// Google Ads (gtag)
	if (config.google_ads && isGtagLoaded()) {
		const w = window as unknown as { gtag: ( ...args: unknown[] ) => void };
		w.gtag( "event", event, {
			send_to: config.google_ads,
			...params,
		} );
	}

	// LinkedIn Insight Tag
	if (config.linkedin && isLinkedInLoaded()) {
		const w = window as unknown as {
			lintrk: ( action: "track", options: { conversion_id?: number } ) => void;
		};
		w.lintrk( "track", { conversion_id: Number(params.conversion_id) || 0 } );
	}
}

/**
 * Convenience wrappers for specific conversion events
 */
export const trackSubscribe = ( email?: string, source?: string ) => {
	trackEvent( "Lead", {
		content_name: "newsletter_subscription",
		content_category: "subscription",
		source: source || "website",
		...( email ? { email } : {} ),
	} );
};

export const trackResourceDownload = ( resourceSlug: string, resourceName: string ) => {
	trackEvent( "Download", {
		content_name: resourceName,
		content_category: "resource_download",
		content_ids: [ resourceSlug ],
	} );
};

export const trackContactSubmit = ( source?: string ) => {
	trackEvent( "Contact", {
		content_name: "contact_form_submit",
		content_category: "lead_generation",
		source: source || "contact_page",
	} );
};

export const trackSearch = ( query: string, resultsCount?: number ) => {
	trackEvent( "Search", {
		search_string: query,
		content_category: "site_search",
		results_count: resultsCount,
	} );
};

/**
 * E-commerce / Lead Gen events for conversion tracking
 */
export const trackViewContent = ( contentId: string, contentType: string, contentName: string, value?: number, currency = "USD" ) => {
	trackEvent( "ViewContent", {
		content_ids: [ contentId ],
		content_type: contentType,
		content_name: contentName,
		value,
		currency,
	} );
};

export const trackAddToRfqBasket = ( productId: string, productName: string, category: string, quantity: number ) => {
	trackEvent( "AddToCart", {
		content_ids: [ productId ],
		content_name: productName,
		content_category: category,
		content_type: "product",
		num_items: quantity,
	} );
};

export const trackInitiateCheckout = ( itemCount: number, totalQuantity: number ) => {
	trackEvent( "InitiateCheckout", {
		num_items: itemCount,
		content_type: "rfq",
		value: undefined,
	} );
};

export const trackRfqSubmit = ( rfqNo: string, itemCount: number, totalQuantity: number ) => {
	trackEvent( "Purchase", {
		transaction_id: rfqNo,
		num_items: itemCount,
		content_type: "rfq",
		value: undefined,
		currency: "USD",
	} );
};

export const trackQuoteRequest = ( serviceType: string, companySize: string ) => {
	trackEvent( "Lead", {
		content_name: "quote_request",
		content_category: "lead_generation",
		service_type: serviceType,
		company_size: companySize,
	} );
};

export const trackPartnerApplication = ( partnerType: string, companyName: string ) => {
	trackEvent( "SubmitApplication", {
		content_name: "partner_application",
		content_category: "partnership",
		partner_type: partnerType,
		company_name: companyName,
	} );
};

export const trackBatteryDiagnostic = ( severity: string, recommendedService: string, confidenceScore: number ) => {
	trackEvent( "Lead", {
		content_name: "battery_diagnostic",
		content_category: "lead_generation",
		diagnosis_severity: severity,
		recommended_service: recommendedService,
		confidence_score: confidenceScore,
	} );
};

export const trackExitIntentPopup = ( action: "shown" | "closed" | "converted" ) => {
	trackEvent( "Lead", {
		content_name: "exit_intent_popup",
		content_category: "conversion_optimization",
		action,
	} );
};
